/**
 * Shared chrome (border + background + shadow + focus styling) for form field
 * primitives. Two visual variants are supported:
 *
 * - `solid` (default): opaque card-like surface with an outer shadow. Used
 *   across the app (forms on pages and in dialogs).
 * - `glass`: translucent, backdrop-blurred, with a subtle inset shadow.
 *   Used on auth/onboarding screens where the form sits on a frosted
 *   `glass-card` background.
 *
 * Each variant exposes three focus-mode class strings because primitives
 * differ in how focus is expressed:
 *
 * - `focusVisible`: most `<input>`/`<textarea>`-like elements.
 * - `focus`: some shadcn primitives (e.g. Radix Select trigger) use the
 *   plain `focus:` pseudo-class.
 * - `focusWithin`: composite primitives where focus lives on an inner
 *   element (e.g. MultiSelect, DateTimePicker wrappers).
 *
 * The raw class strings are declared as literals here so Tailwind's JIT
 * compiler can statically extract every utility used.
 */
export const FIELD_CHROME = {
  solid: {
    base: 'border border-zinc-300 bg-white shadow-md transition-all hover:shadow-lg dark:border-zinc-700 dark:bg-black dark:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] dark:hover:shadow-[0_12px_32px_-12px_rgba(0,0,0,0.6)]',
    focus: 'focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring',
    focusVisible:
      'focus-visible:outline-none focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring',
    focusWithin:
      'focus-within:outline-none focus-within:border-ring focus-within:ring-1 focus-within:ring-ring',
  },
  glass: {
    base: 'border border-zinc-300 bg-white shadow-[0_4px_14px_rgba(15,23,42,0.08)] transition-all dark:border-zinc-600 dark:bg-black dark:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)]',
    focus: 'focus:outline-none focus:border-primary focus:bg-white dark:focus:bg-black',
    focusVisible:
      'focus-visible:outline-none focus-visible:border-primary focus-visible:bg-white dark:focus-visible:bg-black',
    focusWithin:
      'focus-within:outline-none focus-within:border-primary focus-within:bg-white dark:focus-within:bg-black',
  },
} as const;

export type FieldVariant = keyof typeof FIELD_CHROME;
export type FieldFocusMode = 'focus' | 'focusVisible' | 'focusWithin';

/** Resolve the chrome class string for a given variant + focus mode. */
export function fieldChrome(variant: FieldVariant, focus: FieldFocusMode = 'focusVisible'): string {
  const c = FIELD_CHROME[variant];
  return `${c.base} ${c[focus]}`;
}
